import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export interface PinConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  titel: string;
  beschreibung?: string;
  busy: boolean;
  onConfirm: (pin: string) => void;
  errorText?: string | null;
}

export function PinConfirmDialog({
  open,
  onOpenChange,
  titel,
  beschreibung,
  busy,
  onConfirm,
  errorText,
}: PinConfirmDialogProps) {
  const [pin, setPin] = useState("");

  useEffect(() => {
    if (open) {
      setPin("");
    }
  }, [open]);

  const isValidPin = /^\d{6}$/.test(pin);

  const handleOpenChange = (newOpen: boolean) => {
    if (busy) return; // Prevent closing while busy
    onOpenChange(newOpen);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidPin && !busy) {
      onConfirm(pin);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{titel}</DialogTitle>
            {beschreibung && <DialogDescription>{beschreibung}</DialogDescription>}
          </DialogHeader>

          <div className="py-4 space-y-3">
            {errorText && (
              <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-800 dark:border-red-800/40 dark:bg-red-950/30 dark:text-red-300">
                {errorText}
              </div>
            )}
            <div>
              <label htmlFor="pin-input" className="block text-xs font-medium text-ink mb-1">
                6-stellige PIN
              </label>
              <Input
                id="pin-input"
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                disabled={busy}
                placeholder="******"
                className="font-mono text-center tracking-widest text-lg h-10"
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => handleOpenChange(false)}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!isValidPin || busy}
            >
              {busy ? "Wird ausgeführt…" : "Bestätigen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
